// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// __version__ = "1.3.1"
//
// Changelog:
//
// Version 1.3.1:
// - [Removed] `UpdateAddonSeatsPayload` interface as add-on seat management is removed from UI.
// - [Removed] `previewSeatChange` and `confirmSeatChange` functions from the service.
//
// Version 1.3.0:
// - [Added] New functions `previewSeatChange` and `previewPlanChange` to support
//   the two-step "preview and confirm" workflow for subscription modifications.
// - [Added] New function `confirmPlanChange` to finalize a plan upgrade or
//   downgrade after a preview.
// - [Changed] Renamed `updateAddonSeats` to `confirmSeatChange` to accurately
//   reflect its role in the new confirmation workflow.
// - [Removed] The now-obsolete `updateAddonSeats` function.
//
// Version 1.2.2:
// - [Fixed] Reverted the API endpoint path for updating add-on seats to
//   `/my-subscription/seats` to align with the final, stable backend API guide.
// ==============================================================================
// File: src/services/paymentService.ts
import apiClient from './api';
import { CoachSubscription, PaymentTransaction, StripeCheckoutSession, StripeCustomerPortalSession, SubscriptionPlan, SubscriptionChangePreview } from '../types';
import { AxiosError } from 'axios';

// Removed: UpdateAddonSeatsPayload is no longer needed
// export interface UpdateAddonSeatsPayload {
//   quantity: number;
// }

// NEW: Payload for confirming a plan change
export interface PlanChangePayload {
  new_price_id: string;
}

/**
 * Fetches all publicly visible subscription plans.
 */
const getPublicPlans = async (): Promise<SubscriptionPlan[]> => {
  const { data } = await apiClient.get<SubscriptionPlan[]>('/payments/public-plans/');
  return data;
};


const getMySubscription = async (): Promise<CoachSubscription | null> => {
    try {
        const { data } = await apiClient.get<CoachSubscription | null>('/payments/my-subscription');
        return data;
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response && axiosError.response.status === 404) {
            return null; // No active subscription
        }
        throw error;
    }
};

const getMyPaymentTransactions = async (skip = 0, limit = 20): Promise<PaymentTransaction[]> => {
    const { data } = await apiClient.get<PaymentTransaction[]>('/payments/my-payment-transactions', { params: { skip, limit }});
    return data;
};

const createCustomerPortalSession = async (): Promise<StripeCustomerPortalSession> => {
    const { data } = await apiClient.post<StripeCustomerPortalSession>('/payments/stripe/customer-portal/');
    return data;
};

const createCheckoutSession = async (priceId: string): Promise<StripeCheckoutSession> => {
    console.log(`Creating checkout session for price ID: ${priceId}`);
    const { data } = await apiClient.post<StripeCheckoutSession>('/payments/stripe/checkout-session/', { price_id: priceId });
    return data;
};

// --- NEW PREVIEW & CONFIRM WORKFLOW ---

// Removed: previewSeatChange is no longer needed
// const previewSeatChange = async (quantity: number): Promise<SubscriptionChangePreview> => {
//     const { data } = await apiClient.get<SubscriptionChangePreview>('/payments/my-subscription/seats/preview', {
//         params: { quantity }
//     });
//     return data;
// };

// Removed: confirmSeatChange is no longer needed
// const confirmSeatChange = async (payload: UpdateAddonSeatsPayload): Promise<CoachSubscription> => {
//     const { data } = await apiClient.put<CoachSubscription>('/payments/my-subscription/seats', payload);
//     return data;
// };

const previewPlanChange = async (new_price_id: string): Promise<SubscriptionChangePreview> => {
    const { data } = await apiClient.get<SubscriptionChangePreview>('/payments/my-subscription/plan/preview', {
        params: { new_price_id }
    });
    return data;
};

const confirmPlanChange = async (payload: PlanChangePayload): Promise<CoachSubscription> => {
    const { data } = await apiClient.put<CoachSubscription>('/payments/my-subscription/plan', payload);
    return data;
};

export default {
    getPublicPlans,
    getMySubscription,
    getMyPaymentTransactions,
    createCustomerPortalSession,
    createCheckoutSession,
    // Removed: previewSeatChange and confirmSeatChange are no longer exported
    // previewSeatChange,
    // confirmSeatChange,
    previewPlanChange,
    confirmPlanChange,
};